import { useState, useRef, useEffect } from 'react';
import { useEdgeStream } from 'edge-stream-js/react';
import type { IEnvelope } from 'edge-stream-js';
import './EventLogViewer.css';

export interface EventLogViewerProps {
  autoScroll: boolean;
}

interface LogEntry {
  id: string;
  timestamp: Date;
  topic: string;
  serverId: string;
  transport: string;
  protocol: string;
  body: unknown;
  direction: string;
}

export function EventLogViewer({ autoScroll }: EventLogViewerProps) {
  const stream = useEdgeStream();
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [filter, setFilter] = useState('');
  const [selectedLog, setSelectedLog] = useState<LogEntry | null>(null);
  const logsEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const servers = Array.from(stream.servers.values());
    if (servers.length === 0) return;

    const server = servers[0];
    const subscription = server.subscriptionManager.subscribe('*', (envelope: IEnvelope) => {
      const entry: LogEntry = {
        id: envelope.meta.id,
        timestamp: envelope.meta.receivedAt,
        topic: envelope.meta.topic || '(no topic)',
        serverId: envelope.meta.serverId,
        transport: envelope.meta.transport,
        protocol: envelope.meta.protocol,
        body: envelope.body,
        direction: envelope.direction,
      };

      setLogs((prev) => [...prev, entry]);
    });

    return () => subscription.unsubscribe();
  }, [stream]);

  useEffect(() => {
    if (autoScroll && logsEndRef.current) {
      logsEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [logs, autoScroll]);

  const filteredLogs = logs.filter(
    (log) =>
      log.topic.toLowerCase().includes(filter.toLowerCase()) ||
      log.serverId.toLowerCase().includes(filter.toLowerCase()),
  );

  const clearLogs = () => {
    setLogs([]);
    setSelectedLog(null);
  };

  return (
    <div className="event-log-viewer">
      <div className="log-controls">
        <input
          type="text"
          placeholder="Filter by topic or server..."
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="log-filter"
        />
        <div className="log-stats">
          <span className="stat">
            Total: <strong>{logs.length}</strong>
          </span>
          <span className="stat">
            Filtered: <strong>{filteredLogs.length}</strong>
          </span>
        </div>
        <button className="button button-secondary" onClick={clearLogs}>
          Clear Logs
        </button>
      </div>

      <div className="log-container">
        <div className="log-list">
          {filteredLogs.map((log) => (
            <div
              key={log.id}
              className={`log-entry ${selectedLog?.id === log.id ? 'selected' : ''}`}
              onClick={() => setSelectedLog(log)}
            >
              <div className="log-time">{log.timestamp.toLocaleTimeString()}</div>
              <div className="log-topic">{log.topic}</div>
              <div className="log-meta">
                <span className="badge badge-info">{log.transport}</span>
                <span className="badge badge-success">{log.protocol}</span>
              </div>
            </div>
          ))}
          <div ref={logsEndRef} />
        </div>

        {selectedLog && (
          <div className="log-detail">
            <div className="detail-header">
              <h3>Message Details</h3>
              <button
                className="close-btn"
                onClick={() => setSelectedLog(null)}
              >
                ✕
              </button>
            </div>
            <div className="detail-content">
              <div className="detail-field">
                <label>ID:</label>
                <code>{selectedLog.id}</code>
              </div>
              <div className="detail-field">
                <label>Topic:</label>
                <code>{selectedLog.topic}</code>
              </div>
              <div className="detail-field">
                <label>Server:</label>
                <code>{selectedLog.serverId}</code>
              </div>
              <div className="detail-field">
                <label>Transport:</label>
                <code>{selectedLog.transport}</code>
              </div>
              <div className="detail-field">
                <label>Protocol:</label>
                <code>{selectedLog.protocol}</code>
              </div>
              <div className="detail-field">
                <label>Direction:</label>
                <code>{selectedLog.direction}</code>
              </div>
              <div className="detail-field">
                <label>Timestamp:</label>
                <code>{selectedLog.timestamp.toISOString()}</code>
              </div>
              <div className="detail-field full-width">
                <label>Body:</label>
                <pre className="json-viewer">
                  {JSON.stringify(selectedLog.body, null, 2)}
                </pre>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
